import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Modal, Segmented, Tooltip } from 'antd';
import { DesktopOutlined, ExpandOutlined, EyeOutlined, MobileOutlined } from '@ant-design/icons';
import { PREVIEW_MESSAGES, PREVIEW_PATH } from './previewProtocol';

const DEVICES = {
  desktop: { width: 1280, label: 'Desktop' },
  phone: { width: 390, label: 'Phone' },
};

/**
 * The real public form page in an iframe, fed with the editor's unsaved state.
 * The page renders at the device's true width and is scaled down to fit, so layout,
 * breakpoints and font sizes match what applicants see.
 *
 * data: { form: { application_text, submit_text }, layout, fields }
 * focusField: field_name to scroll into view (the question being edited)
 */
const PreviewFrame = ({ data, device, focusField, title }) => {
  const boxRef = useRef(null);
  const frameRef = useRef(null);
  const readyRef = useRef(false);
  const dataRef = useRef(data);
  const [box, setBox] = useState({ width: 0, height: 0 });

  const focusRef = useRef(focusField);
  dataRef.current = data;
  focusRef.current = focusField;

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => setBox({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const post = useCallback((message) => {
    const win = frameRef.current?.contentWindow;
    if (win && readyRef.current) win.postMessage(message, window.location.origin);
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      if (event.data?.type === PREVIEW_MESSAGES.ready) {
        readyRef.current = true;
        post({ type: PREVIEW_MESSAGES.data, data: dataRef.current });
        // once the page has rendered, show the question being edited
        if (focusRef.current) setTimeout(() => post({ type: PREVIEW_MESSAGES.focus, fieldName: focusRef.current, instant: true }), 300);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [post]);

  useEffect(() => {
    post({ type: PREVIEW_MESSAGES.data, data });
  }, [data, post]);

  useEffect(() => {
    if (focusField) post({ type: PREVIEW_MESSAGES.focus, fieldName: focusField });
  }, [focusField, post]);

  const deviceWidth = DEVICES[device].width;
  const scale = box.width ? Math.min(1, box.width / deviceWidth) : 1;
  const frameHeight = box.height ? box.height / scale : 800;

  return (
    <div className={`dfw-frame dfw-frame--${device}`} ref={boxRef}>
      <div className="dfw-frame__stage" style={{ width: deviceWidth * scale, height: box.height || undefined }}>
        <iframe
          ref={frameRef}
          title={title}
          src={PREVIEW_PATH}
          style={{
            width: deviceWidth,
            height: frameHeight,
            transform: `scale(${scale})`,
          }}
        />
      </div>
    </div>
  );
};

const LivePreview = ({ data, focusField, caption, defaultDevice = 'desktop' }) => {
  const [device, setDevice] = useState(defaultDevice);
  const [expanded, setExpanded] = useState(false);

  const toolbar = (
    <>
      <Segmented
        size="small"
        value={device}
        onChange={setDevice}
        options={[
          { value: 'desktop', label: 'Desktop', icon: <DesktopOutlined aria-hidden="true" /> },
          { value: 'phone', label: 'Phone', icon: <MobileOutlined aria-hidden="true" /> },
        ]}
        aria-label="Preview size"
      />
      <span className="dfw-grow" />
      <Tooltip title="The preview shows the page as applicants see it. Nothing in it can be filled in or sent.">
        <span className="dfw-preview__viewonly"><EyeOutlined aria-hidden="true" /> View only</span>
      </Tooltip>
    </>
  );

  return (
    <section className="dfw-preview" aria-label="Live preview">
      <header className="dfw-preview__bar">
        {toolbar}
        <Tooltip title="Open a larger preview">
          <Button size="small" type="text" icon={<ExpandOutlined aria-hidden="true" />} onClick={() => setExpanded(true)} aria-label="Open a larger preview" />
        </Tooltip>
      </header>
      {caption && <p className="dfw-preview__caption">{caption}</p>}
      <PreviewFrame data={data} device={device} focusField={focusField} title="Form preview" />

      <Modal
        open={expanded}
        onCancel={() => setExpanded(false)}
        footer={null}
        width="min(1360px, 96vw)"
        rootClassName="do-scope dfw-preview-modal"
        title="Preview"
        destroyOnHidden
        centered
      >
        <div className="dfw-preview dfw-preview--large">
          <header className="dfw-preview__bar">{toolbar}</header>
          <PreviewFrame data={data} device={device} focusField={focusField} title="Form preview, large" />
        </div>
      </Modal>
    </section>
  );
};

export default LivePreview;
